import type { FC } from 'react';
import { StyleSheet, Text, View, type StyleProp, type ViewStyle } from 'react-native';
import type ApiPromotion from 'src/core/models/api/ApiPromotion';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Styles from 'src/core/utils/Styles';
import Translations from 'src/core/utils/Translations';
import { SecondaryButton } from 'src/presentation/components/molecules/Button';
import CardLabel from 'src/presentation/components/molecules/CardLabel';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import { HtmlView } from 'src/presentation/components/organisms/HtmlView';
import usePromotionActions from 'src/presentation/hooks/usePromotionActions';

interface Props {
	promotion: ApiPromotion | null;
	style?: StyleProp<ViewStyle>;
}

const PromotionCard: FC<Props> = ({ promotion, style }) => {
	const { openPromotion } = usePromotionActions();

	if (promotion === null) {
		return null;
	}

	const hasTitle = promotion.title !== null && promotion.title !== '';
	const hasText = promotion.text !== null && promotion.text !== '';

	if (!hasTitle && !hasText) {
		return null;
	}

	const image = promotion.image !== null && promotion.image !== '' ? promotion.image : null;
	const hasBadge = image !== null && promotion.sticker_text !== null && promotion.sticker_text !== '';
	const hasLink = promotion.button_link !== null && promotion.button_link !== '';
	const hasButton = promotion.button_action !== 'none' && (promotion.button_action !== 'external' || hasLink);

	return (
		<View style={[styles.card, Styles.card.shadowSoft, style]}>
			{image !== null ? (
				<View style={styles.image}>
					<ThermenImage contentFit={'cover'} source={{ uri: image }} style={styles.imageFill} />
					{hasBadge ? (
						<CardLabel icon={promotion.sticker_icon ?? undefined} text={promotion.sticker_text ?? ''} variant="dark" />
					) : null}
				</View>
			) : null}

			<View style={styles.body}>
				{hasTitle ? <Text style={styles.title}>{promotion.title}</Text> : null}
				{hasText ? <HtmlView html={promotion.text ?? ''} textStyle={styles.text} /> : null}
			</View>

			{hasButton ? (
				<View style={styles.footer}>
					<SecondaryButton label={promotion.button_text ?? Translations.moreInfo} onPress={() => openPromotion(promotion)} />
				</View>
			) : null}
		</View>
	);
};

const styles = StyleSheet.create({
	card: {
		backgroundColor: Colors.white,
		borderRadius: Dimens.borderRadius4,
		overflow: 'hidden',
	},
	image: {
		width: '100%',
		aspectRatio: Dimens.aspectRatio16By9,
		backgroundColor: Colors.gray400,
	},
	imageFill: {
		width: '100%',
		height: '100%',
	},
	body: {
		padding: Dimens.padding20,
		gap: Dimens.gap8,
	},
	text: {
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight22,
	},
	title: {
		color: Colors.gray900,
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize18,
		lineHeight: Dimens.lineHeight24,
	},
	footer: {
		paddingBottom: Dimens.padding20,
		paddingHorizontal: Dimens.padding20,
	},
});

export default PromotionCard;
